/**
 * Navigation Module
 * Handles mobile menu and dropdown navigation
 */
export class Navigation {
    constructor() {
        this.mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
        this.mobileMenu = document.querySelector('[data-mobile-menu]');
        this.dropdownItems = document.querySelectorAll('[data-nav-dropdown]');
        this.init();
    }
    init() {
        this.initMobileMenu();
        this.initDropdowns();
        this.initStickyHeader();
        this.initCurrencySwitcher();
        this.initMegaMenu();
    }
    initMobileMenu() {
        if (!this.mobileMenuToggle || !this.mobileMenu)
            return;
        this.mobileMenuToggle.addEventListener('click', () => {
            this.toggleMobileMenu();
        });
        // Close on escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.mobileMenu?.classList.contains('is-open')) {
                this.closeMobileMenu();
            }
        });
        // Close on overlay click
        const overlay = document.querySelector('[data-mobile-menu-overlay]');
        overlay?.addEventListener('click', () => {
            this.closeMobileMenu();
        });
    }
    toggleMobileMenu() {
        const isOpen = this.mobileMenu?.classList.toggle('is-open');
        this.mobileMenuToggle?.classList.toggle('is-active', isOpen);
        document.body.classList.toggle('mobile-menu-open', isOpen);
        const overlay = document.querySelector('[data-mobile-menu-overlay]');
        overlay?.classList.toggle('is-visible', isOpen);
    }
    closeMobileMenu() {
        this.mobileMenu?.classList.remove('is-open');
        this.mobileMenuToggle?.classList.remove('is-active');
        document.body.classList.remove('mobile-menu-open');
        const overlay = document.querySelector('[data-mobile-menu-overlay]');
        overlay?.classList.remove('is-visible');
    }
    initDropdowns() {
        this.dropdownItems.forEach((item) => {
            const trigger = item.querySelector('[data-nav-dropdown-trigger]');
            const menu = item.querySelector('[data-nav-dropdown-menu]');
            if (!trigger || !menu)
                return;
            // Desktop: hover
            if (window.innerWidth >= 1024) {
                item.addEventListener('mouseenter', () => {
                    this.openDropdown(item);
                });
                item.addEventListener('mouseleave', () => {
                    this.closeDropdown(item);
                });
            }
            // Keyboard navigation
            trigger.addEventListener('keydown', (e) => {
                const keyEvent = e;
                if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                    e.preventDefault();
                    this.toggleDropdown(item);
                }
            });
            // Mobile: click
            trigger.addEventListener('click', (e) => {
                if (window.innerWidth < 1024) {
                    e.preventDefault();
                    this.toggleDropdown(item);
                }
            });
        });
    }
    openDropdown(item) {
        // Close other dropdowns
        this.dropdownItems.forEach((other) => {
            if (other !== item) {
                this.closeDropdown(other);
            }
        });
        item.classList.add('is-open');
        const trigger = item.querySelector('[data-nav-dropdown-trigger]');
        trigger?.setAttribute('aria-expanded', 'true');
    }
    closeDropdown(item) {
        item.classList.remove('is-open');
        const trigger = item.querySelector('[data-nav-dropdown-trigger]');
        trigger?.setAttribute('aria-expanded', 'false');
    }
    toggleDropdown(item) {
        if (item.classList.contains('is-open')) {
            this.closeDropdown(item);
        }
        else {
            this.openDropdown(item);
        }
    }
    initStickyHeader() {
        const header = document.querySelector('[data-sticky-header]');
        if (!header)
            return;
        let lastScroll = 0;
        const threshold = 100;
        window.addEventListener('scroll', () => {
            const currentScroll = window.pageYOffset;
            if (currentScroll > threshold) {
                header.classList.add('is-sticky');
                if (currentScroll > lastScroll && currentScroll > 200) {
                    // Scrolling down
                    header.classList.add('is-hidden');
                }
                else {
                    // Scrolling up
                    header.classList.remove('is-hidden');
                }
            }
            else {
                header.classList.remove('is-sticky', 'is-hidden');
            }
            lastScroll = currentScroll;
        }, { passive: true });
    }
    initCurrencySwitcher() {
        const currencySelect = document.querySelector('[data-currency-select]');
        if (!currencySelect)
            return;
        currencySelect.addEventListener('change', (e) => {
            const target = e.target;
            const currency = target.value;
            // Update URL with new currency parameter
            const url = new URL(window.location.href);
            url.searchParams.set('currency', currency);
            // Navigate to the new URL
            window.location.href = url.toString();
        });
    }
    initMegaMenu() {
        const megaMenuTriggers = document.querySelectorAll('[data-mega-menu-trigger]');
        megaMenuTriggers.forEach((trigger) => {
            const navItem = trigger.closest('[data-nav-item]');
            const megaMenu = navItem?.querySelector('[data-mega-menu]');
            if (!navItem || !megaMenu)
                return;
            let closeTimeout = null;
            // Open on hover (desktop)
            navItem.addEventListener('mouseenter', () => {
                if (window.innerWidth >= 1024) {
                    if (closeTimeout) {
                        clearTimeout(closeTimeout);
                        closeTimeout = null;
                    }
                    // Close other mega menus
                    document.querySelectorAll('[data-nav-item].mega-menu-open').forEach((item) => {
                        if (item !== navItem) {
                            item.classList.remove('mega-menu-open');
                            item.querySelector('[data-mega-menu-trigger]')?.setAttribute('aria-expanded', 'false');
                        }
                    });
                    navItem.classList.add('mega-menu-open');
                    trigger.setAttribute('aria-expanded', 'true');
                }
            });
            // Close on mouse leave with delay
            navItem.addEventListener('mouseleave', () => {
                if (window.innerWidth >= 1024) {
                    closeTimeout = setTimeout(() => {
                        navItem.classList.remove('mega-menu-open');
                        trigger.setAttribute('aria-expanded', 'false');
                    }, 150);
                }
            });
            // Toggle on click (mobile and accessibility)
            trigger.addEventListener('click', (e) => {
                if (window.innerWidth < 1024) {
                    e.preventDefault();
                    const isOpen = navItem.classList.toggle('mega-menu-open');
                    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
                }
            });
            // Keyboard navigation
            trigger.addEventListener('keydown', (e) => {
                const keyEvent = e;
                if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                    e.preventDefault();
                    const isOpen = navItem.classList.toggle('mega-menu-open');
                    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
                }
                if (keyEvent.key === 'Escape') {
                    navItem.classList.remove('mega-menu-open');
                    trigger.setAttribute('aria-expanded', 'false');
                    trigger.focus();
                }
            });
        });
        // Close all mega menus when clicking outside
        document.addEventListener('click', (e) => {
            const target = e.target;
            if (!target.closest('[data-nav-item]')) {
                document.querySelectorAll('[data-nav-item].mega-menu-open').forEach((item) => {
                    item.classList.remove('mega-menu-open');
                    item.querySelector('[data-mega-menu-trigger]')?.setAttribute('aria-expanded', 'false');
                });
            }
        });
    }
}
export default Navigation;
